import * as THREE from 'three';

/**
 * WeaponBuilder — assembles hundreds of small bevelled parts into a handful of
 * SkinnedMeshes.
 *
 * Why skinning for a rigid gun? Because the draw-call budget for the whole
 * viewmodel is 12, and a rifle needs a dozen independently animated pieces (bolt,
 * charging handle, dust cover, magazine, trigger, selector, safety, hammer...).
 * Binding vertices to bones gives full articulation at one draw call per material —
 * which is exactly how a shipped FPS viewmodel is built. Rigid parts bind to a
 * single bone; the gloved hands bind with real weights so a finger is one
 * continuous surface across its three joints instead of a stack of capsules.
 *
 * Every part is baked into bind-pose space, then given:
 *   - triplanar-projected UVs at a constant texel density, so a tiled detail
 *     normal map has consistent scale across the entire merged mesh;
 *   - `aWear`  — chamfer / rub polish, from how far the vertex normal sits from
 *     the part's primary axes;
 *   - `aGrime` — carbon fouling, from distance to the declared soot sources
 *     (ejection port, gas block, muzzle);
 *   - `aAO`    — baked cavity occlusion against a voxelisation of every part, so
 *     rail slots, the magwell throat and the space under the optic are darker
 *     than the surfaces around them without costing a texture unit.
 *   - `aTouch` — hand/sling/magazine contact, from distance to the declared contact
 *     sources. This is where the weapon's roughness FIELD comes from: a carried
 *     rifle is burnished where it is held and matt everywhere else, and that is a
 *     spatial roughness variation with a cause rather than noise (FINDINGS F20 #2).
 */

const _m3 = new THREE.Matrix3();
const _v = new THREE.Vector3();

export class WeaponBuilder {
  constructor(opts = {}) {
    this.uvScale = opts.uvScale ?? 42; // 1 m -> 42 tiles of detail normal
    // Global trim on the authored wear values. Edge polish reads enormously strong at
    // viewmodel distance, so the per-part numbers are authored generously and scaled
    // down once, here, rather than re-tuned in three hundred call sites.
    this.wearScale = opts.wearScale ?? 0.42;
    /* The CHAMFER component is scaled separately from the whole-part `wear:` base.
     *
     * They were one number, and 0.42 was fitted while the chamfer detector was still
     * firing on lathes and lofts — a curved part that reports itself as one long
     * chamfer really does have to be trimmed hard. The `boxy` guard fixed that at
     * source, and the trim then stayed and quietly capped a real 0.45-authored
     * receiver chamfer at aWear 0.19, which after the shader's quadratic albedo
     * weight is a 3.6 % mix toward bare metal — i.e. invisible. That is why
     * FINDINGS F20 reports "nothing in the build has been knocked" about a weapon
     * that has an edge-wear channel on 130 of its parts. */
    this.edgeWearScale = opts.edgeWearScale ?? 0.90;
    this.bones = [];
    this.boneByName = new Map();
    this.buckets = new Map(); // matKey -> { geos: [], }
    this.root = null;
    /** Carbon-fouling sources, weapon space: { p:[x,y,z], r0, r1, k }. */
    this.soot = [];
    /**
     * HAND-CONTACT sources, weapon space. Same shape as `soot` and for the same
     * reason: a field that is a function of position, evaluated once at build time,
     * costs nothing at runtime and needs no texture unit (FINDINGS F6).
     *
     * FINDINGS F20 named "roughness that varies across a single surface" as the gap,
     * and the honest source of it on a weapon is not noise — it is CONTACT. A rifle
     * that has been carried is burnished exactly where a hand, a sling, a plate
     * carrier and a magazine touch it, and matt everywhere else. Those places are
     * known: the pistol grip, the handguard where the support hand wraps, the
     * charging-handle latch, the selector, the mag release, the magwell mouth the
     * magazine is slammed into, the top of the buffer tube where the cheek sits.
     */
    this.touch = [];
    /** Part AABBs, used to bake cavity AO at build time. */
    this._boxes = [];
    this.ao = opts.ao !== false;
    this.aoCell = opts.aoCell ?? 0.0032;
  }

  /** Declare a bone. Positions are in bind-pose weapon space (parent-relative). */
  bone(name, parent = null, position = [0, 0, 0], quat = null) {
    const b = new THREE.Bone();
    b.name = name;
    b.position.set(position[0], position[1], position[2]);
    if (quat) b.quaternion.copy(quat);
    if (parent) {
      const p = this.boneByName.get(parent);
      if (!p) throw new Error(`WeaponBuilder: unknown parent bone "${parent}"`);
      p.add(b);
    } else {
      if (!this.root) this.root = b;
      else this.root.add(b);
    }
    this.bones.push(b);
    this.boneByName.set(name, b);
    b.userData.index = this.bones.length - 1;
    return b;
  }

  /** Declare a bone from a world-space (bind pose) matrix. */
  boneAt(name, parent, world) {
    const p = parent ? this.boneByName.get(parent) : null;
    const local = new THREE.Matrix4();
    if (p) {
      this.sync();
      local.copy(p.matrixWorld).invert().multiply(world);
    } else {
      local.copy(world);
    }
    const pos = new THREE.Vector3(), quat = new THREE.Quaternion(), scl = new THREE.Vector3();
    local.decompose(pos, quat, scl);
    return this.bone(name, parent, [pos.x, pos.y, pos.z], quat);
  }

  /** Refresh bind-pose world matrices so `bone.matrixWorld` can be read. */
  sync() {
    if (this.root) this.root.updateMatrixWorld(true);
    return this;
  }

  worldOf(name) {
    this.sync();
    return this.boneByName.get(name).matrixWorld;
  }

  /** Declare a carbon-fouling source. Full strength inside r0, gone by r1. */
  sootSource(x, y, z, r0, r1, k = 1) {
    this.soot.push({ x, y, z, r0, r1, k });
    return this;
  }

  _sootAt(x, y, z) {
    return fieldAt(this.soot, x, y, z);
  }

  /** Declare a hand-contact / burnish source. Full strength inside r0, gone by r1. */
  touchSource(x, y, z, r0, r1, k = 1) {
    this.touch.push({ x, y, z, r0, r1, k });
    return this;
  }

  _touchAt(x, y, z) {
    return fieldAt(this.touch, x, y, z);
  }

  /**
   * @param {THREE.BufferGeometry|THREE.BufferGeometry[]} geo
   * @param {string} matKey  material bucket
   * @param {string} boneName
   * @param {THREE.Matrix4} [matrix] placement, bind-pose weapon space
   * @param {{wear?:number, edgeWear?:boolean|number, grime?:number, uvScale?:number,
   *          keepUV?:boolean, noAO?:boolean, noSoot?:boolean,
   *          skin?:(x:number,y:number,z:number)=>Array<[number,number]>}} [opts]
   */
  add(geo, matKey, boneName, matrix = null, opts = {}) {
    if (Array.isArray(geo)) {
      for (const g of geo) this.add(g, matKey, boneName, matrix, opts);
      return this;
    }
    if (!geo) return this;
    const bone = this.boneByName.get(boneName);
    if (!bone) throw new Error(`WeaponBuilder: unknown bone "${boneName}"`);

    let g = geo.index ? geo.toNonIndexed() : geo;
    if (!g.attributes.normal) g.computeVertexNormals();
    if (matrix) {
      g = g.clone();
      g.applyMatrix4(matrix);
    } else if (g === geo) {
      g = g.clone();
    }

    const count = g.attributes.position.count;
    const pos = g.attributes.position.array;
    const nrm = g.attributes.normal.array;
    // Anything carrying authored atlas UVs is a decal: it must not occlude, and
    // it inherits the fouling of the surface it is printed on rather than its own.
    if (opts.keepUV) opts = { ...opts, noAO: true, noSoot: true };

    // --- consistent-density triplanar UVs -----------------------------------
    const s = opts.uvScale ?? this.uvScale;
    if (opts.keepUV && g.attributes.uv) {
      // decals and anything else carrying authored atlas coordinates
    } else {
      const uv = new Float32Array(count * 2);
      for (let i = 0; i < count; i++) {
        const nx = Math.abs(nrm[i * 3]), ny = Math.abs(nrm[i * 3 + 1]), nz = Math.abs(nrm[i * 3 + 2]);
        const x = pos[i * 3], y = pos[i * 3 + 1], z = pos[i * 3 + 2];
        if (nx >= ny && nx >= nz) { uv[i * 2] = z * s; uv[i * 2 + 1] = y * s; }
        else if (ny >= nz) { uv[i * 2] = x * s; uv[i * 2 + 1] = z * s; }
        else { uv[i * 2] = x * s; uv[i * 2 + 1] = y * s; }
      }
      g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    }

    /* --- edge wear -----------------------------------------------------------
     *
     * The chamfer detector is "how far is this vertex normal from an axis" — which
     * is exactly right for the bevelled boxes and extrusions that make up most of a
     * weapon, and exactly WRONG for a lathe or a loft, where half of every cylinder
     * sits at 45 degrees to the axes and therefore reported itself as a chamfer.
     * That is why the barrel, the handguard tube and the optic body used to be
     * uniformly "worn": the wear channel mixes the albedo toward bare metal and the
     * metalness toward 1, so a falsely-worn curved part becomes a bright grey mirror.
     * Measured before this guard: mean aWear 0.133 across the whole receiver mesh,
     * which multiplied the effective diffuse albedo by ~4x.
     *
     * A part is classified by what fraction of its vertices ARE axis-aligned; a
     * bevelled box is ~0.85, a 20-segment cylinder is ~0.2. Curved parts keep a
     * fraction of the effect so their end chamfers still catch a highlight.
     */
    const wear = new Float32Array(count);
    const ws = this.wearScale;
    const base = (opts.wear ?? 0) * ws;
    if (opts.edgeWear) {
      let aligned = 0;
      for (let i = 0; i < count; i++) {
        const mx = Math.max(Math.abs(nrm[i * 3]), Math.abs(nrm[i * 3 + 1]), Math.abs(nrm[i * 3 + 2]));
        if (mx > 0.97) aligned++;
      }
      const boxy = aligned / count >= 0.45;
      const amt = (typeof opts.edgeWear === 'number' ? opts.edgeWear : 1)
        * this.edgeWearScale * (boxy ? 1 : 0.22);
      for (let i = 0; i < count; i++) {
        const mx = Math.max(Math.abs(nrm[i * 3]), Math.abs(nrm[i * 3 + 1]), Math.abs(nrm[i * 3 + 2]));
        const t = smooth01((1 - mx - 0.06) / 0.18);
        wear[i] = Math.min(1, base + t * amt);
      }
    } else if (base) {
      wear.fill(base);
    }
    g.setAttribute('aWear', new THREE.BufferAttribute(wear, 1));

    // --- carbon fouling -----------------------------------------------------
    const grime = new Float32Array(count);
    const gBase = opts.grime ?? 0;
    if (!opts.noSoot && (this.soot.length || gBase)) {
      for (let i = 0; i < count; i++) {
        const gg = this.soot.length
          ? this._sootAt(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]) : 0;
        grime[i] = Math.min(1, gBase + gg);
      }
    } else if (gBase) {
      grime.fill(Math.min(1, gBase));
    }
    g.setAttribute('aGrime', new THREE.BufferAttribute(grime, 1));

    // --- hand contact / burnish --------------------------------------------
    const touch = new Float32Array(count);
    const tBase = opts.touch ?? 0;
    if (this.touch.length || tBase) {
      for (let i = 0; i < count; i++) {
        const tv = this.touch.length
          ? this._touchAt(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]) : 0;
        touch[i] = Math.min(1, tBase + tv);
      }
    }
    g.setAttribute('aTouch', new THREE.BufferAttribute(touch, 1));

    // --- cavity AO + sky openness (both baked in build()) -------------------
    const ao = new Float32Array(count);
    ao.fill(1);
    g.setAttribute('aAO', new THREE.BufferAttribute(ao, 1));
    const sky = new Float32Array(count);
    sky.fill(1);
    g.setAttribute('aSky', new THREE.BufferAttribute(sky, 1));
    g.userData.noAO = !!opts.noAO;

    // --- skin binding -------------------------------------------------------
    const idx = new Uint16Array(count * 4);
    const wgt = new Float32Array(count * 4);
    if (opts.chain && g.attributes.aS) {
      /*
       * Chain skinning for the gloved hands.
       *
       * `aS` is the vertex's parameter along the bone chain — integer values sit
       * exactly on a joint. Segment `j` is driven by bone `chain[j]`; within
       * `blend` of a joint the two neighbouring bones share the vertex 50/50 at
       * the joint itself and fall off linearly. That is what turns three rigid
       * capsules into one continuous finger with no ring seam at the knuckle,
       * which is the defect the critique called out by name.
       */
      const sArr = g.attributes.aS.array;
      const bidx = opts.chain.map((n) => this.boneIndex(n));
      const last = bidx.length - 1;
      const BL = opts.chainBlend ?? 0.34;
      for (let i = 0; i < count; i++) {
        let s = sArr[i];
        if (s < 0) s = 0;
        if (s > last + 1) s = last + 1;
        let j = Math.floor(s);
        if (j > last) j = last;
        const t = s - j;
        let other = -1, w = 0;
        if (t < BL && j > 0) { other = j - 1; w = 0.5 * (1 - t / BL); }
        else if (t > 1 - BL && j < last) { other = j + 1; w = 0.5 * ((t - (1 - BL)) / BL); }
        idx[i * 4] = bidx[j];
        wgt[i * 4] = 1 - w;
        if (other >= 0) { idx[i * 4 + 1] = bidx[other]; wgt[i * 4 + 1] = w; }
      }
    } else {
      const bi = bone.userData.index;
      for (let i = 0; i < count; i++) { idx[i * 4] = bi; wgt[i * 4] = 1; }
    }
    g.setAttribute('skinIndex', new THREE.BufferAttribute(idx, 4));
    g.setAttribute('skinWeight', new THREE.BufferAttribute(wgt, 4));

    // strip anything else so the merge stays homogeneous
    for (const k of Object.keys(g.attributes)) {
      if (!ATTRS.includes(k)) g.deleteAttribute(k);
    }
    g.morphAttributes = {};

    // Record the AABB for the cavity-AO voxelisation.
    //
    // `occlude: false` marks a part that should be *shaded* by the bake but must
    // not *cast* into it. A curled finger or a forearm cone has an axis-aligned
    // box several times its own volume, and letting those boxes occlude buries the
    // whole hand in false AO — measured mean 0.30 (the floor) before this opt-out.
    if (this.ao && !opts.noAO && opts.occlude !== false) {
      let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
      for (let i = 0; i < count; i++) {
        const x = pos[i * 3], y = pos[i * 3 + 1], z = pos[i * 3 + 2];
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (y < y0) y0 = y; if (y > y1) y1 = y;
        if (z < z0) z0 = z; if (z > z1) z1 = z;
      }
      this._boxes.push([x0, y0, z0, x1, y1, z1]);
    }

    if (!this.buckets.has(matKey)) this.buckets.set(matKey, []);
    this.buckets.get(matKey).push(g);
    return this;
  }

  /** Convenience: add pre-skinned raw geometry that already carries a matrix. */
  addAt(geo, matKey, boneName, x, y, z, opts) {
    return this.add(geo, matKey, boneName, new THREE.Matrix4().makeTranslation(x, y, z), opts);
  }

  boneIndex(name) {
    const b = this.boneByName.get(name);
    return b ? b.userData.index : 0;
  }

  triangleCount() {
    let n = 0;
    for (const list of this.buckets.values()) {
      for (const g of list) n += g.attributes.position.count / 3;
    }
    return n;
  }

  /**
   * Bake per-vertex cavity occlusion.
   *
   * Every part contributes its AABB to a coarse occupancy grid; each vertex then
   * fires a small hemisphere of short rays and counts how many are blocked. Boxes
   * rather than triangles keeps this at boot-time cost we can afford (~10 M grid
   * probes for a whole rifle) while still resolving the features that matter — the
   * gaps between Picatinny cleats, the magwell throat, the shelf under the optic —
   * because those gaps really are the space between separate parts.
   */
  _bakeAO() {
    const boxes = this._boxes;
    if (!boxes.length) return;
    let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
    for (const b of boxes) {
      if (b[0] < x0) x0 = b[0]; if (b[1] < y0) y0 = b[1]; if (b[2] < z0) z0 = b[2];
      if (b[3] > x1) x1 = b[3]; if (b[4] > y1) y1 = b[4]; if (b[5] > z1) z1 = b[5];
    }
    const c = this.aoCell;
    const pad = c * 2;
    x0 -= pad; y0 -= pad; z0 -= pad; x1 += pad; y1 += pad; z1 += pad;
    const nx = Math.ceil((x1 - x0) / c), ny = Math.ceil((y1 - y0) / c), nz = Math.ceil((z1 - z0) / c);
    if (nx * ny * nz > 12e6) return;   // refuse to spend the memory; AO stays 1
    const grid = new Uint8Array(nx * ny * nz);
    const at = (i, j, k) => grid[(k * ny + j) * nx + i];

    for (const b of boxes) {
      const i0 = Math.max(0, Math.floor((b[0] - x0) / c));
      const j0 = Math.max(0, Math.floor((b[1] - y0) / c));
      const k0 = Math.max(0, Math.floor((b[2] - z0) / c));
      const i1 = Math.min(nx - 1, Math.floor((b[3] - x0) / c));
      const j1 = Math.min(ny - 1, Math.floor((b[4] - y0) / c));
      const k1 = Math.min(nz - 1, Math.floor((b[5] - z0) / c));
      for (let k = k0; k <= k1; k++) {
        for (let j = j0; j <= j1; j++) {
          const row = (k * ny + j) * nx;
          for (let i = i0; i <= i1; i++) grid[row + i] = 1;
        }
      }
    }

    // A fixed, deterministic hemisphere of directions in tangent space.
    const DIRS = [
      [0, 0, 1], [0.82, 0, 0.57], [-0.82, 0, 0.57], [0, 0.82, 0.57], [0, -0.82, 0.57],
      [0.58, 0.58, 0.57], [-0.58, 0.58, 0.57], [0.58, -0.58, 0.57], [-0.58, -0.58, 0.57],
    ];
    /*
     * Openness toward weapon +Y, over a 45 degree cone and a long march.
     *
     * A carried weapon's key light arrives from above it in nearly every frame, so
     * this is a stable, animation-proof stand-in for the one self-shadow the
     * critique named: "the optic casts nothing onto the rail directly below it".
     * A real shadow map for `ctx.viewScene` is not available to this module —
     * `renderer.shadowMap.autoUpdate` is globally false (lighting owns that), so a
     * viewmodel caster would render its map once and then freeze.
     */
    const SKY = [
      [0, 1, 0], [0.60, 0.80, 0], [-0.60, 0.80, 0], [0, 0.80, 0.60], [0, 0.80, -0.60],
    ];
    const STEPS = 5;
    const SKY_STEPS = 9;
    const step = c * 2.2;
    const t = new THREE.Vector3(), bt = new THREE.Vector3(), n = new THREE.Vector3();

    const marchBlocked = (px, py, pz, dx, dy, dz, steps) => {
      for (let sI = 1; sI <= steps; sI++) {
        const q = sI * step;
        const i = ((px + dx * q) - x0) / c | 0;
        const j = ((py + dy * q) - y0) / c | 0;
        const k = ((pz + dz * q) - z0) / c | 0;
        if (i < 0 || j < 0 || k < 0 || i >= nx || j >= ny || k >= nz) return 0;
        if (at(i, j, k)) return 1 - (sI - 1) / steps;
      }
      return 0;
    };

    for (const list of this.buckets.values()) {
      for (const g of list) {
        if (g.userData.noAO) continue;
        const p = g.attributes.position.array;
        const nr = g.attributes.normal.array;
        const aoA = g.attributes.aAO.array;
        const skyA = g.attributes.aSky.array;
        const cnt = g.attributes.position.count;
        for (let v = 0; v < cnt; v++) {
          n.set(nr[v * 3], nr[v * 3 + 1], nr[v * 3 + 2]);
          if (n.lengthSq() < 1e-8) { aoA[v] = 1; skyA[v] = 1; continue; }
          n.normalize();
          // build a tangent frame
          if (Math.abs(n.z) < 0.9) t.set(-n.y, n.x, 0).normalize();
          else t.set(1, 0, 0).addScaledVector(n, -n.x).normalize();
          bt.crossVectors(n, t);
          const px = p[v * 3] + n.x * c, py = p[v * 3 + 1] + n.y * c, pz = p[v * 3 + 2] + n.z * c;

          let blocked = 0;
          for (let d = 0; d < DIRS.length; d++) {
            const D = DIRS[d];
            blocked += marchBlocked(px, py, pz,
              t.x * D[0] + bt.x * D[1] + n.x * D[2],
              t.y * D[0] + bt.y * D[1] + n.y * D[2],
              t.z * D[0] + bt.z * D[1] + n.z * D[2], STEPS);
          }
          // keep a floor so cavities read dark but never black
          aoA[v] = 0.42 + 0.58 * (1 - blocked / DIRS.length);

          let skyBlocked = 0, skyCount = 0;
          for (let d = 0; d < SKY.length; d++) {
            const D = SKY[d];
            if (D[0] * n.x + D[1] * n.y + D[2] * n.z <= 0.12) continue;
            skyCount++;
            skyBlocked += marchBlocked(px, py, pz, D[0], D[1], D[2], SKY_STEPS);
          }
          skyA[v] = skyCount ? 0.34 + 0.66 * (1 - skyBlocked / skyCount) : 1;
        }
        g.attributes.aAO.needsUpdate = true;
        g.attributes.aSky.needsUpdate = true;
      }
    }
  }

  /**
   * @param {(key:string)=>THREE.Material} materialFor
   * @returns {{ group: THREE.Group, meshes: THREE.SkinnedMesh[], skeleton: THREE.Skeleton, boneByName: Map }}
   */
  build(materialFor) {
    const group = new THREE.Group();
    group.name = 'weaponRig';

    if (this.ao) {
      try { this._bakeAO(); } catch (e) { console.warn('[weapons] AO bake skipped:', e); }
    }

    // Bind pose: bones are already positioned, so world matrices define the inverses.
    if (this.root) {
      group.add(this.root);
      this.root.updateMatrixWorld(true);
    }
    const skeleton = new THREE.Skeleton(this.bones);

    const meshes = [];
    for (const [key, list] of this.buckets) {
      const geo = mergeHomogeneous(list);
      const mat = materialFor(key);
      const mesh = new THREE.SkinnedMesh(geo, mat);
      mesh.name = `wpn_${key}`;
      mesh.frustumCulled = false;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.bindMode = THREE.AttachedBindMode;
      group.add(mesh);
      mesh.bind(skeleton, new THREE.Matrix4());
      meshes.push(mesh);
    }

    for (const g of this.buckets.values()) for (const x of g) x.dispose?.();
    this.buckets.clear();
    this._boxes.length = 0;

    return { group, meshes, skeleton, boneByName: this.boneByName, bones: this.bones, root: this.root };
  }
}

function smooth01(t) {
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return t * t * (3 - 2 * t);
}

/** Squared-falloff scalar field from a list of {x,y,z,r0,r1,k} sources. */
function fieldAt(list, x, y, z) {
  let g = 0;
  for (let i = 0; i < list.length; i++) {
    const s = list[i];
    const d = Math.hypot(x - s.x, y - s.y, z - s.z);
    if (d >= s.r1) continue;
    const t = d <= s.r0 ? 1 : 1 - (d - s.r0) / (s.r1 - s.r0);
    const v = s.k * t * t;
    if (v > g) g = v;
  }
  return g > 1 ? 1 : g;
}

const ATTRS = ['position', 'normal', 'uv', 'aWear', 'aGrime', 'aAO', 'aSky', 'aTouch', 'skinIndex', 'skinWeight'];

/** Fast merge for geometries known to share the exact same attribute layout. */
export function mergeHomogeneous(list) {
  let total = 0;
  for (const g of list) total += g.attributes.position.count;
  const out = new THREE.BufferGeometry();
  const spec = [
    ['position', 3, Float32Array], ['normal', 3, Float32Array], ['uv', 2, Float32Array],
    ['aWear', 1, Float32Array], ['aGrime', 1, Float32Array], ['aAO', 1, Float32Array],
    ['aSky', 1, Float32Array], ['aTouch', 1, Float32Array],
    ['skinIndex', 4, Uint16Array], ['skinWeight', 4, Float32Array],
  ];
  for (const [name, size, Ctor] of spec) {
    const arr = new Ctor(total * size);
    let o = 0;
    for (const g of list) {
      const a = g.attributes[name];
      arr.set(a.array, o * size);
      o += a.count;
    }
    out.setAttribute(name, new THREE.BufferAttribute(arr, size));
  }
  out.computeBoundingSphere();
  out.computeBoundingBox();
  return out;
}

/** Matrix helper: translate + euler rotate (XYZ) + uniform/np scale. */
export function place(x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, sx = 1, sy = sx, sz = sx) {
  return new THREE.Matrix4().compose(
    new THREE.Vector3(x, y, z),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz, 'XYZ')),
    new THREE.Vector3(sx, sy, sz),
  );
}
